// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeListItem } from '@/billing/api/types';
import { STATUS_CHIP_CONFIG } from '@/billing/components/charge-status-constants';
import { useSetChargeBillableState } from '@/billing/hooks/use-set-charge-billable-state';
import { FormDatePicker } from '@/components/form-date-picker';
import { StatusChip } from '@/components/status-chip';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({
  expectedBillableDate: dateSchema
    .nullable()
    .refine((value) => value !== null && value.isValid, {
      message: 'Expected billable date is required',
    }),
});

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  charge: ChargeListItem | null;
  onClose: () => void;
  onSuccess?: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

export function MarkChargeNotBillableDialog(props: Props) {
  const charge = props.charge;
  const plannedChipProps = STATUS_CHIP_CONFIG.PLANNED;

  const seedDate =
    charge?.expectedBillableDate ?? DateTime.now().startOf('day');

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      expectedBillableDate: seedDate,
    },
  });

  // Re-seed the form whenever the dialog opens or the target charge changes.
  // The render-at-root pattern keeps this component mounted across opens, so
  // without this effect a second open shows the previously edited value
  // instead of the charge's current schedule.
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    reset({
      expectedBillableDate:
        charge?.expectedBillableDate ?? DateTime.now().startOf('day'),
    });
  }, [props.isOpen, charge, reset]);

  const mutation = useSetChargeBillableState({
    onSuccess: () => {
      props.onSuccess?.();
      props.onClose();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      props.onClose();
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  const watchedDate = watch('expectedBillableDate');
  const showPastWarning =
    DateTime.isDateTime(watchedDate) &&
    !Number.isNaN(watchedDate.toMillis()) &&
    watchedDate.startOf('day') <= DateTime.now().startOf('day');

  const onSubmit = (data: FormData) => {
    if (!charge) {
      return;
    }
    if (
      data.expectedBillableDate === null ||
      Number.isNaN(data.expectedBillableDate.toMillis())
    ) {
      return;
    }
    mutation.mutate({
      chargeKey: charge.key,
      markBillable: false,
      expectedBillableDate: data.expectedBillableDate,
    });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={props.onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle
          sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
        >
          Mark charge as not billable
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          <Stack spacing={2.5} pt={2}>
            <Typography
              variant="body1"
              component="div"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.75,
              }}
            >
              <span>This will return</span>
              <strong>{charge?.label ?? ''}</strong>
              <span>to</span>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <StatusChip
                  label={plannedChipProps.label}
                  variant={plannedChipProps.variant}
                  color={plannedChipProps.color}
                />
              </Box>
              <span>status.</span>
            </Typography>

            <FormDatePicker
              name="expectedBillableDate"
              control={control}
              label="Expected billable date"
              isDisabled={mutation.isPending}
            />

            {showPastWarning && (
              <Alert severity="warning">
                The expected billing date is in the past, so the charge will
                become billable in the next daily billing run. To prevent the
                charge from becoming billable, update the expected billing date
                to the future.
              </Alert>
            )}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <Button
            onClick={props.onClose}
            variant="outlined"
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={mutation.isPending}
            disabled={!isValid || charge === null}
            sx={{ ml: 1.5 }}
          >
            Mark not billable
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
